import React, { FC, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/form.module.scss'
import text from 'styles/utility/text.module.scss'
import classNames from 'classnames'
import { Spacer } from 'components/Utility'
import { SelectBox } from './index'
import { SelectOption } from 'types/common'
import { useTimesWithRange } from 'hooks/store'
import { useEarliestBusinessTime } from 'hooks/businessHour'
import { DayOfWeekType } from 'lib/daysOfWeek'
import { useSelector } from 'react-redux'
import { State } from 'index'
import { getOrderStartTime } from 'reducks/store/selectors'
import { StoreCalendarType } from 'types/storeCalendar'
import { parseDateToDeadline, parseTimeToDeadline, parseTimeToNumber } from 'lib/helper'
import { useSelectCallback } from 'hooks/form'
import { FirebaseDocRefType } from 'firebase/index'

type Props = {
  daysOfWeek: DayOfWeekType[]
  handleHoursToDeadline?: (dateToDeadline: string, timeToDeadline: string, ref?: FirebaseDocRefType) => void
  prevHoursToDeadline?: number
  scheduleRef?: FirebaseDocRefType
  temporaryOpen?: StoreCalendarType
}

const HoursToDeadlineForm: FC<Props> = (props) => {
  const { daysOfWeek, handleHoursToDeadline, prevHoursToDeadline, scheduleRef, temporaryOpen } = props

  const orderStartTime = useSelector((state: State) => getOrderStartTime(state))
  const [dateToDeadline, setDateToDeadline] = useState<string>('0')
  const [timeToDeadline, setTimeToDeadline] = useState<string>('00:00')
  const selectDateToDeadline = useSelectCallback(setDateToDeadline)
  const selectTimeToDeadline = useSelectCallback(setTimeToDeadline)
  const earliestOrderStartTime = useEarliestBusinessTime(daysOfWeek, orderStartTime, 'order_start_time', temporaryOpen)

  const dateToDeadlineOptions = useMemo(() => {
    const options: SelectOption[] = [{ label: `当日`, value: '0' }]
    for (let i = 1; i < 15; i = (i + 1) | 0) {
      options.push({ label: `${i}日前`, value: String(i) })
    }
    return options
  }, [])

  // 15分毎で24時間分のリストを作る
  // 原則、当日N時の注文締切はオーダー開始時間までが範囲
  // 当日N時のオーダー開始時刻以降が選択済の場合は、選択済の時刻までを選択範囲とする
  const timeToDeadlineOptions = useTimesWithRange(
    '00:00',
    dateToDeadline === '0'
      ? parseTimeToNumber(timeToDeadline) > parseTimeToNumber(earliestOrderStartTime)
        ? timeToDeadline
        : earliestOrderStartTime
      : '23:45'
  )

  // 受渡可能曜日が変更された際、当日締切時刻がオーダー開始時刻より遅ければオーダー開始時刻に合わせる
  useEffect(() => {
    if (dateToDeadline !== '0' || parseTimeToNumber(timeToDeadline) <= parseTimeToNumber(earliestOrderStartTime)) return
    setTimeToDeadline(earliestOrderStartTime)
    handleHoursToDeadline?.(dateToDeadline, earliestOrderStartTime, scheduleRef)
  }, [earliestOrderStartTime])

  useEffect(() => {
    if (typeof prevHoursToDeadline === 'undefined') return
    setDateToDeadline(parseDateToDeadline(prevHoursToDeadline))
    setTimeToDeadline(parseTimeToDeadline(prevHoursToDeadline))
  }, [prevHoursToDeadline])

  return (
    <div>
      <div className={styles.column_two}>
        <div className={classNames(styles.column_two__item, text.font_small)}>締め切り日</div>
        <Spacer size={8} />
        <div className={classNames(styles.column_two__item, text.font_small)}>締め切り時間</div>
      </div>
      <Spacer size={8} />
      <div className={styles.column_two}>
        <div className={styles.column_two__item}>
          <SelectBox
            options={dateToDeadlineOptions}
            select={(value) => {
              selectDateToDeadline(value)
              // 当日が選択された際、締切時刻がオーダー開始時刻より遅ければオーダー開始時刻に合わせる
              if (value === '0' && parseTimeToNumber(timeToDeadline) > parseTimeToNumber(earliestOrderStartTime)) {
                setTimeToDeadline(earliestOrderStartTime)
                handleHoursToDeadline?.(value, earliestOrderStartTime, scheduleRef)
              } else {
                handleHoursToDeadline?.(value, timeToDeadline, scheduleRef)
              }
            }}
            value={dateToDeadline}
          />
        </div>
        <Spacer size={8} />
        <div className={styles.column_two__item}>
          <SelectBox
            options={timeToDeadlineOptions}
            select={(value) => {
              selectTimeToDeadline(value)
              handleHoursToDeadline?.(dateToDeadline, value, scheduleRef)
            }}
            value={timeToDeadline}
          />
        </div>
      </div>
    </div>
  )
}

export default HoursToDeadlineForm
